import { createTheme } from '@mui/material/styles';
import { colors } from '@mui/material';
import typography from './typography';
import './index.css';

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536
    }
  },

  palette: {
    background: {
      dark: '#F4F6F8',
      default: colors.common.white,
      paper: colors.common.white
    },
    primary: {
      main: '#066E82',
      dark: colors.common.black,
      light: colors.common.white
    },
    secondary: {
      main: colors.red[700]
    },
    text: {
      primary: colors.blueGrey[900],
      secondary: colors.blueGrey[600]
    },
    customBackground: {
      main: '#BDBDBD'
    },
    opacityBackground: {
      main: 'rgba(255, 255, 255, 0.16)'
    },
    item: {
      color: 'var(--text-primary, #212529)'
    },
    select: '#000',
    textBase: '#000000',
    textSecondary: '#666666',
    AccentGreen: '#1B6224',
    SecondaryAccentGreen: '#1E8479',
    Error: '#7F2223',
    ComponentStroke: '#CCCCCC',
    FieldStroke: '#CCCCCC',
    BackgroundOffWhite: '#F6F6F6',
    BackgroundWhite: '#FFFFFF',
    SelectedColor: '#1E8479',
    HighlightBlue: 'rgba(50, 72, 168, 0.08)',
    HighlightGreen: 'rgba(76, 175, 80, 0.08)',
    SurfacesWhite: '#FFFFFF',
    TextBlack: '#000000',
    DialogMessage: '#49454F',
    DialogTitle: '#1C1B1F',
    Neutrals: '#6C7087',
    DeepSpaceSparkle: '#4D626D',
    DavysGrey: '4F535E',
    Orange: '#C13830',
    BlueBayoux: '#4D626D',
    Indigo: '#3F51B5',
    Red: '#D9490F',
    Gainsboro: '#DCDCDC',
    WhiteBorder: '#E9E9E9',
    Cultured: '#F6F7F7',
    CongoPink: '#252527 ',
    RedOpacity: 'rgba(255, 0, 0, 0.1)',
    DarkCyan: 'rgba(30, 132, 121, 0.08)',
    TitleColor: '#212529',
    NumberColor: '#3F51B5',
    SummaryColor: '#FFF4F4',
    lightestGray: '#E6E9F0',
    errorColor: 'red',
    TypographyPrimary: '#1D1B20',
    TypographyTitle: '#171717',
    ColorSurfaceStroke: '#C5C7CA',
    whiteBackground: '#FFFFFF',
    secondaryText: '1D1B20',
    medGray: '#828797',
    TypographySecondary: '#4D4E54',
    navBorder: 'transparent',
    ColorFrostedSurfaceLight: 'rgba(10, 10, 10, 0.03)',
    disabled: '#cccccc'
  },
  typography,
  components: {
    startIcon: {
      opacity: 0
    }
  },
  overrides: {
    MuiCssBaseline: {
      '*': {
        fontFamily: 'Overused Grotesk'
      },
      body: {
        fontFamily: 'Overused Grotesk',
        color: 'black'
      }
    },
    MuiAppBar: {
      colorPrimary: {
        backgroundColor: 'white',
        borderBottom: '1px solid black',
        color: 'black'
      }
    },
    MuiStepButton: {
      root: {
        '@media (max-width: 640px)': {
          gap: '16px',
          justifyContent: 'start'
        }
      }
    },
    MuiButton: {
      textSecondary: {
        color: '#263238'
      },
      label: {
        fontFamily: 'Overused Grotesk'
      },
      containedPrimary: {
        color: 'white',
        backgroundColor: 'black',
        border: '1px solid black',
        '&:hover': {
          color: 'white',
          backgroundColor: 'black',
          border: '1px solid white'
        }
      },
      outlinedPrimary: {
        color: 'black',
        backgroundColor: 'white',
        border: '1px solid black',
        '&:hover': {
          color: 'white',
          backgroundColor: 'black',
          border: '1px solid white'
        }
      },
      outlinedSecondary: {
        color: '#C13830',
        backgroundColor: 'white',
        border: '1px solid #C13830',
        '&:hover': {
          color: 'white',
          backgroundColor: '#C13830',
          border: '1px solid white'
        }
      }
    },
    MuiTab: {
      wrapper: {
        fontFamily: 'Overused Grotesk'
      }
    },
    MuiInputBase: {
      input: {
        fontFamily: 'Overused Grotesk'
      }
    },
    MuiTableCell: {
      root: {
        fontFamily: 'Overused Grotesk'
      }
    },
    MuiTypography: {
      h1: {
        fontFamily: 'Overused Grotesk',
        color: 'black'
      },
      h2: {
        fontFamily: 'Overused Grotesk',
        color: 'red'
      },
      h3: {
        fontFamily: 'Overused Grotesk',
        fontSize: '34px',
        fontStyle: 'normal',
        fontWeight: 350,
        lineHeight: '48px'
      },
      h4: {
        fontFamily: 'Overused Grotesk',
        color: '#066E82',
        fontSize: '28px',
        fontStyle: 'normal',
        fontWeight: 350,
        lineHeight: '36px'
      },
      h5: {
        fontFamily: 'Overused Grotesk',
        color: 'black'
      },
      h6: {
        fontFamily: 'Overused Grotesk',
        color: 'black'
      },
      subtitle1: {
        fontFamily: 'Overused Grotesk',
        color: '#171717',
        fontSize: '20px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '26px'
      },
      subtitle2: {
        fontFamily: 'Overused Grotesk',
        color: '#171717',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '24px'
      },
      body1: {
        fontFamily: 'Overused Grotesk',
        color: '#252527',
        fontSize: '24px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '32px'
      },
      body2: {
        fontFamily: 'Overused Grotesk',
        color: '#1D1B20',
        fontSize: '20px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '26px'
      },
      caption: {
        fontFamily: 'Overused Grotesk',
        color: 'black'
      }
    }
  }
});

export default theme;
